import { AuthRole } from './couch-auth';

export interface GenerateActivationKeyParams {
  role: AuthRole;
  validFrom_utc?: string;
  validUntil_utc?: string;
  multiUseCount?: number /** Number of users that can activate the same key. Used for invite keys only. */;
  /** Test keys function just like normal ones, but such keys are eligible for automatic deletion and user entitlements based on
   * those keys for automatic revocation at regular maintenance intervals. */
  testKey?: boolean;
}

export interface GenerateActivationKeyResults {
  error: string; // empty if all is ok
  code?: string;
}

export interface UseActivationKeyParams {
  code: string;
}

/**
 * Why a redemption was refused, in a form code can test (#6984). The English `error`
 * text stays the user-facing message on the Redeem Code page; this is what other
 * callers branch on, so rewording a message never changes behaviour.
 */
export enum ActivationKeyErrorKind {
  /** the typed text is not a 16-character key at all */
  Malformed = 'malformed',
  /** no such key, or a damaged key record */
  Invalid = 'invalid',
  /** this caller redeemed this same key within the server's RECENT_REDEMPTION_WINDOW_HOURS (24) and their
   *  account records it: an earlier attempt of this same redemption whose response was lost */
  AlreadyActivatedByCaller = 'already-activated-by-caller',
  /** this caller's key record was written but their user record was not (found on a retry within that window,
   *  or when the user write itself failed); support must repair it. The server reports it to Sentry itself */
  HalfActivated = 'half-activated',
  /** used before: by someone else (however recently), or by this caller outside that window, or in a
   *  redemption their account has since moved past (a newer key) */
  AlreadyActivated = 'already-activated',
  /** the key's role is below the account's role */
  NotApplicable = 'not-applicable',
  /** the key would not extend the account's existing key licence */
  WouldNotExtend = 'would-not-extend',
  /** the key's validity starts later; see `notValidForDays` */
  NotYetValid = 'not-yet-valid',
  /** the key's validity window has ended */
  Expired = 'expired',
  /** a write conflict; trying again later may work */
  Temporary = 'temporary',
  /** anything else that went wrong */
  Unexpected = 'unexpected',
}

export interface UseActivationKeyResults {
  role?: AuthRole /** role that was activated */;
  licenseExpires_utc?: string /** how long is safe to cache the license */;
  error: string /** empty if all is ok. A redemption succeeded only when this is empty; never infer success from a missing errorKind */;
  /** set beside every non-empty `error` */
  errorKind?: ActivationKeyErrorKind;
  /** for NotYetValid: whole days until the key can be used (the number the message prints) */
  notValidForDays?: number;
  retry?: boolean /** In case of couch update conflict (multiple users registering the same key) client may want to retry */;
}

export enum UnsubscribeReason {
  HighPrice = 1,
  NoLongerNeed = 2,
  NotSatisfied = 3,
  SwitchToAnother = 4,
  NotPriority = 5,
  Mistake = 6,
  AnotherPlan = 8,  // NB: There is no 7!
  Other = 0,
};

export interface PayProUnsubscribeParams {
  cancellationReasonId: UnsubscribeReason;
  reasonText?: string; // The custom reason for the subscription cancellation request. It is required if cancellationReasonId is not passed.
};

export interface PayProUnsubscribeResult {
  error?: string;
  errorDetails?: any;
  alreadyCanceled?: boolean;
};

export interface PayProResubscribeParams {
  reasonText?: string; // The custom reason for the resubscription request. 
};

export interface PayProResubscribeResult {
  error?: string;
};

export interface DownloadUserDbParams {
  userId?: string;
  email?: string;
  /** CouchDB _id cursor: rows strictly greater than this _id are returned.
   *  Omit on the first call; pass the previous response's nextLastSeenKey
   *  on subsequent calls. */
  lastSeenKey?: string;
  /** Soft cap on docs per page. Server clamps to [1, 1000]; the page may
   *  also be cut short by the server's byte-size budget. */
  limit?: number;
}
export interface DownloadUserDbResult {
  data?: any;
  /** Cursor for the next page (the last `_id` returned in `data`), or null
   *  when this is the last page. Callers stop paginating when this is null. */
  nextLastSeenKey?: string | null;
  error?: string; // empty if all is ok
}